/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { clone, cloneDeep } from 'lodash';
import { DraggableLocation, DragUpdate } from 'react-beautiful-dnd';
import { DragType, DragSubType } from '../models/constants';
import DragUpdateGenerated from '../models/DragUpdateGenerated';
import Group from '../models/Group';
import DragItem from '../models/DragItem';
import GroupedAnalysis from '../models/GroupedAnalysis';
import GroupInterfaceWithNullableItem from '../models/GroupInterfaceWithNullableItem';
import { formatTitle, saveAnalyses, updateSortOrder } from './groupsUtilities';

const getSourceGroupIndex = (
  ev: DragUpdate | DragUpdateGenerated,
  groups: Group[]
): number => {
  const {
    source: { droppableId },
  } = ev;
  const groupTitle = droppableId;
  const groupNames: string[] = groups.map((g) => formatTitle(g.title));
  return groupNames.indexOf(groupTitle);
};

const getDestinationGroupIndex = (
  groupTitle: string,
  groups: Group[]
): number => {
  const groupNames: string[] = groups.map((g) => formatTitle(g.title));
  return groupNames.indexOf(groupTitle);
};

export const determineDragType = (
  ev: DragUpdate | DragUpdateGenerated
): DragType => {
  const { source, destination } = ev;
  const affectedGroups = new Set([
    source.droppableId,
    (destination as DraggableLocation).droppableId,
  ]);

  return affectedGroups.size === 1 ? 'insideOneGroup' : 'fromGroupToGroup';
};

export const determineDragSubType = (
  selectedIDs: number[],
  ev: DragUpdate | DragUpdateGenerated,
  groups: Group[]
): DragSubType => {
  const {
    source: { index },
  } = ev;
  const sourceIndex = getSourceGroupIndex(ev, groups);
  const { contentThreadId } = groups[sourceIndex].items[index];

  return selectedIDs.length <= 1 || selectedIDs.indexOf(contentThreadId) === -1
    ? 'singledrag'
    : 'multidrag';
};

export const isDragUpdate = (ev: DragUpdate | DragUpdateGenerated): boolean => {
  return 'mode' in ev;
};

export const updateSingleDragBetweenGroups = (
  groups: Group[],
  ev: DragUpdate | DragUpdateGenerated
): DragItem => {
  const clonedGroups = cloneDeep(groups);
  const { source, destination } = ev;
  const groupTitle = (destination as DraggableLocation).droppableId;
  const destGroupIdx = getDestinationGroupIndex(groupTitle, groups);
  const srcGroupIdx = getSourceGroupIndex(ev, groups);

  const item = clone(clonedGroups[srcGroupIdx].items[source.index]);
  item.isSelected = false;

  clonedGroups[srcGroupIdx].items = clonedGroups[srcGroupIdx].items.filter(
    (i) => i.contentThreadId !== item.contentThreadId
  );

  clonedGroups[destGroupIdx].items.splice(
    (destination as DraggableLocation).index,
    0,
    item
  );
  clonedGroups[destGroupIdx].contentCount += 1;
  clonedGroups[srcGroupIdx].contentCount -= 1;

  updateSortOrder(clonedGroups);
  return {
    updatedGroups: clonedGroups,
    destGroupId: clonedGroups[destGroupIdx].groupId,
    destinationIndex: (destination as DraggableLocation).index,
    contentThreadIds: [item.contentThreadId],
  };
};

export const updateSingleDragInsideOneGroup = (
  groups: GroupInterfaceWithNullableItem[],
  ev: DragUpdate | DragUpdateGenerated
): DragItem => {
  const clonedGroups = cloneDeep(groups);
  const { source, destination } = ev;
  const destGroupIndex = (destination as DraggableLocation).index;
  const srcGroupIndex = getSourceGroupIndex(ev, groups);

  const group = clonedGroups[srcGroupIndex];
  const itemsToMove = group.items.splice(source.index, 1);
  group.items.splice(destGroupIndex, 0, itemsToMove[0]);

  updateSortOrder(clonedGroups);
  let destGroupId = group.groupId;
  if (!destGroupId) {
    destGroupId = group.contentThreadId ? group.contentThreadId : -1;
  }
  return {
    updatedGroups: clonedGroups,
    destGroupId,
    destinationIndex: destGroupIndex,
    contentThreadIds: itemsToMove.map((i) => i.contentThreadId),
  };
};

export const updateMultiDragInsideOneGroup = (
  groups: Group[],
  ev: DragUpdate | DragUpdateGenerated
): DragItem => {
  const clonedGroups = cloneDeep(groups);
  const { destination, source } = ev;
  const { index: destinationItemIndex, droppableId } =
    destination as DraggableLocation;
  const selectedItems: GroupedAnalysis[] = [];
  groups.forEach((group, groupIndex) => {
    group.items.forEach((item) => {
      if (item.isSelected) {
        selectedItems.push(item);
        clonedGroups[groupIndex].items = clonedGroups[groupIndex].items.filter(
          (i) => i.contentThreadId !== item.contentThreadId
        );
        clonedGroups[groupIndex].contentCount -= 1;
      }
    });
  });
  const destinationGroupIndex = getDestinationGroupIndex(
    droppableId,
    clonedGroups
  );
  // if the user drags down
  if (destinationItemIndex > source.index) {
    clonedGroups[destinationGroupIndex].items.splice(
      destinationItemIndex - (selectedItems.length - 1),
      0,
      ...selectedItems
    );
  } else {
    clonedGroups[destinationGroupIndex].items.splice(
      destinationItemIndex,
      0,
      ...selectedItems
    );
  }
  clonedGroups[destinationGroupIndex].contentCount += selectedItems.length;
  // eslint-disable-next-line no-restricted-syntax
  for (const item of selectedItems) {
    item.isSelected = false;
  }

  updateSortOrder(clonedGroups);

  let destGroupId = clonedGroups[destinationGroupIndex].groupId;
  if (!destGroupId) {
    destGroupId = clonedGroups[destinationGroupIndex].contentThreadId
      ? (clonedGroups[destinationGroupIndex].contentThreadId as number)
      : -1;
  }
  return {
    updatedGroups: clonedGroups,
    destGroupId,
    destinationIndex: destinationItemIndex,
  };
};

export const updateMultiDragBetweenGroups = (
  groups: Group[],
  ev: DragUpdate | DragUpdateGenerated,
  selectedIDs: number[]
): DragItem => {
  const clonedGroups = cloneDeep(groups);
  const { destination } = ev;
  const { droppableId: groupTitle, index } = destination as DraggableLocation;
  const destGroupIndex = getDestinationGroupIndex(groupTitle, groups);
  let destinationItemIndex = index;

  const analysisToMove: {
    analysis: GroupedAnalysis;
    groupIndex: number;
  }[] = [];
  clonedGroups.forEach((group, i) => {
    group.items.forEach((item) => {
      if (selectedIDs.includes(item.contentThreadId)) {
        analysisToMove.push({ analysis: item, groupIndex: i });
        group.contentCount -= 1;
      }
    });
  });

  analysisToMove.forEach((item) => {
    const { analysis, groupIndex } = item;
    item.analysis.isSelected = false;
    clonedGroups[groupIndex].items = clonedGroups[groupIndex].items.filter(
      (i) => i.contentThreadId !== analysis.contentThreadId
    );
  });

  analysisToMove.forEach((item) => {
    clonedGroups[destGroupIndex].items.splice(
      destinationItemIndex,
      0,
      item.analysis
    );
    destinationItemIndex += 1;
  });

  clonedGroups[destGroupIndex].contentCount += analysisToMove.length;
  updateSortOrder(clonedGroups);
  return {
    updatedGroups: clonedGroups,
    destGroupId: clonedGroups[destGroupIndex].groupId,
    destinationIndex: index,
  };
};

export const saveSingleAnalyses = async (
  projectId: string,
  groups: Group[],
  selectedIDs: number[],
  destinationGroupId: number,
  destinationIndex: number,
  isKfa = false
): Promise<boolean> => {
  try {
    await saveAnalyses(
      projectId,
      groups,
      selectedIDs,
      destinationGroupId,
      destinationIndex,
      isKfa
    );
    return true;
  } catch (error) {
    return false;
  }
};

export const saveMultipleAnalyses = async (
  projectId: string,
  groups: Group[],
  selectedIDs: number[],
  destinationGroupId: number,
  destinationIndex: number,
  isKfa = false
): Promise<boolean> => {
  try {
    await saveAnalyses(
      projectId,
      groups,
      selectedIDs,
      destinationGroupId,
      destinationIndex,
      isKfa
    );
    return true;
  } catch (error) {
    return false;
  }
};
